// @ts-strict-ignore
import { IsThreeJsType } from '@3d-scene/common/util/is-threejs-type';
import { BufferGeometry, DoubleSide, Euler, FrontSide, Material, Mesh, Vector2, Vector3 } from 'three';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { Connection } from '@3d-scene/model3d/connection';
import { ConnectionAreaVO, MetaElementVO } from '@shared/vo/meta-element.vo';
import { ConnectionConfiguration, ConnectionDefinition } from './connection-configuration';
import { MetaElementUtils } from './meta-element.utils';
import { ConnectionAreaType, ConnectionCreator, ConnectionShapeType } from './connection-creator';
import { ArticleVO } from '../../../model/vo/article.vo';
import { StringUtils } from '@shared/util/string-utils';
import { Object3DUtils } from '@3d-scene/common/util/object-3d-utils';
import { CloneUtils } from '@shared/util/clone-utils';
import { SpriteMaterial } from '@3d-scene/material/sprite-material';
import { Injectable } from '@angular/core';
import { ConnectionInfoUtils } from '@3d-scene/model3d/util/connection-info-utils';
import { MeshUtils } from '@3d-scene/common/util/mesh-utils';

@Injectable({ providedIn: 'root' })
export class ConnectionConverter {
  constructor(private _connectionCreator: ConnectionCreator) {}

  public convertConnection(originalConnection: Connection, newConnectionDefinition: ConnectionDefinition): Connection[] {
    if (!originalConnection || !newConnectionDefinition) {
      return [originalConnection];
    }
    return this.convertConnectionByType(originalConnection, newConnectionDefinition);
  }

  public convertConnectionForm(originalConnection: Connection, newConnectionForm: ConnectionShapeType): void {
    if (!originalConnection || !newConnectionForm || this.isConnectionOfFormType(originalConnection, newConnectionForm)) {
      return;
    }
    const linkedConnections: Connection[] = this._connectionCreator.getMultipurposeConnectionsBySelection(originalConnection);
    linkedConnections.forEach((c) => this.continueConvertConnectionForm(c, newConnectionForm));
  }

  public applyCustomShapeToConnection(originalConnection: Connection, shapeConnectionPoints: Vector2[]): void {
    if (!originalConnection || !shapeConnectionPoints || shapeConnectionPoints.length < 3) {
      return;
    }
    const linkedConnections: Connection[] = this._connectionCreator.getMultipurposeConnectionsBySelection(originalConnection);
    linkedConnections.forEach(c => this.continueApplyCustomShapeToConnection(c, shapeConnectionPoints));
  }

  private convertConnectionByType(originalConnection: Connection, newConnectionDefinition: ConnectionDefinition): Connection[] {
    const connectionConfigurationsToCreate: ConnectionConfiguration[] = [];
    const articleContainer: ArticleContainer = originalConnection.articleContainer;

    const linkedConnections: MetaElementVO[] = this._connectionCreator.getMultipurposeAreasBySelection(originalConnection);
    const keepOriginalConnectionName: boolean =
      !newConnectionDefinition.name && this._connectionCreator.isMultipurposeAreaType(newConnectionDefinition.type);

    const mainConnectionConfiguration: ConnectionConfiguration = this.createMainConnectionConfiguration(
      newConnectionDefinition,
      linkedConnections,
      originalConnection,
      keepOriginalConnectionName
    );
    connectionConfigurationsToCreate.push(mainConnectionConfiguration);

    const internalConnectionConfiguration: ConnectionConfiguration = this.createInternalConnectionConfiguration(
      newConnectionDefinition,
      linkedConnections,
      originalConnection,
      keepOriginalConnectionName
    );
    if (internalConnectionConfiguration) {
      connectionConfigurationsToCreate.push(internalConnectionConfiguration);

      // process specific case when texture area converted to multipurpose area and name of linked texture area should be kept
      // so internal postfix needs to be applied to the main (connection) area
      if (mainConnectionConfiguration.connectionName === internalConnectionConfiguration.connectionName) {
        mainConnectionConfiguration.applyInternalConnectionPostfix();
      }

      MetaElementUtils.applySetIdToMultipurposeAreas(mainConnectionConfiguration.metaElement, internalConnectionConfiguration.metaElement);
    }

    this.removeConnection(originalConnection);
    const createdConnections: Connection[] = [];
    for (let i: number = 0; i < connectionConfigurationsToCreate.length; i++) {
      const configurationToCreate: ConnectionConfiguration = connectionConfigurationsToCreate[i];

      if (configurationToCreate.metaElement instanceof ConnectionAreaVO && originalConnection.metaElementVO instanceof ConnectionAreaVO) {
        configurationToCreate.metaElement.connectionMultiplicity = originalConnection.metaElementVO.connectionMultiplicity;
      }

      MetaElementUtils.addMetaElementToArticle(articleContainer.article, configurationToCreate.metaElement);
      articleContainer.addChildToModel(configurationToCreate.connectionMesh);
      const connection: Connection = articleContainer.findConnectionByName(configurationToCreate.connectionName);
      const widthOrRadius: number = connection.meshIsCircle ? originalConnection.meshRadius : originalConnection.meshWidth;
      const length: number = connection.meshIsCircle ? null : originalConnection.meshLength;
      connection.clearMeshValues();
      connection.normalizeMesh();
      connection.mesh.position.copy(new Vector3());
      connection.mesh.rotation.copy(new Euler());

      let connectionRotation: Vector3 = originalConnection.meshRotationDegree;
      if (this.isConnectionsOfDifferentTypes(connection, originalConnection)) {
        connectionRotation = this._connectionCreator.addInternalRotation(connection, connectionRotation);
      }
      connection.updateMesh(originalConnection.meshPosition, connectionRotation, widthOrRadius, length);
      createdConnections.push(connection);
    }
    articleContainer.refreshMetaElements();
    return createdConnections;
  }

  private isConnectionsOfDifferentTypes(connection1: Connection, connection2: Connection): boolean {
    return (
      this._connectionCreator.getConnectionTypeByMetaElement(connection1.metaElementVO, false) !==
      this._connectionCreator.getConnectionTypeByMetaElement(connection2.metaElementVO, false)
    );
  }

  private continueConvertConnectionForm(originalConnection: Connection, newConnectionForm: ConnectionShapeType): void {
    let size: number = 0;
    let newMeshGeometry: BufferGeometry;
    if (newConnectionForm === ConnectionShapeType.CIRCLE) {
      size = originalConnection.meshWidth / 2;
      newMeshGeometry = this._connectionCreator.createCircleConnection(size, originalConnection.articleContainer).geometry;
    } else {
      size = originalConnection.meshRadius * 2;
      newMeshGeometry = this._connectionCreator.createRectangleConnection(
        originalConnection.meshRadius * 2,
        originalConnection.meshRadius * 2,
        originalConnection.articleContainer
      ).geometry;
    }
    this.applyMeshGeometryToConnection(originalConnection, newMeshGeometry, size, newConnectionForm === ConnectionShapeType.CIRCLE ? null : size);
  }

  private continueApplyCustomShapeToConnection(originalConnection: Connection, shapeConnectionPoints: Vector2[]): void {
    const newMeshGeometry: BufferGeometry = ConnectionCreator.createCustomShapeConnection(
        shapeConnectionPoints,
        originalConnection.articleContainer
    ).geometry;
    this.applyMeshGeometryToConnection(originalConnection, newMeshGeometry);
  }

  private applyMeshGeometryToConnection(connection: Connection, meshGeometry: BufferGeometry, widthOrRadius?: number, length?: number): void {
    const meshPosition: Vector3 = connection.meshPosition;
    const meshRotation: Vector3 = connection.meshRotationDegree;
    connection.clearMeshValues();
    connection.normalizeMesh();
    if (connection.mesh.geometry) {
      connection.mesh.geometry.dispose();
    }
    connection.mesh.geometry = meshGeometry;
    connection.setMesh(connection.mesh);
    connection.updateMesh(meshPosition, meshRotation, widthOrRadius, length);
  }

  private removeConnection(connection: Connection): void {
    const articleContainer: ArticleContainer = connection.articleContainer;
    const linkedConnections: Connection[] = this._connectionCreator.getMultipurposeConnectionsBySelection(connection);
    linkedConnections.forEach((c) => {
      articleContainer.disposeConnectionByName(c.connectionName);
      this.removeMetaElementByName(articleContainer.article, c.connectionName);
    });
  }

  public removeMetaElementByName(article: ArticleVO, name: string): void {
    if (article && article.metaElements && StringUtils.isNotEmpty(name)) {
      article.metaElements = article.metaElements.filter((mel) => mel.name !== name);
    }
  }

  private createMainConnectionConfiguration(
    targetConnectionDefinition: ConnectionDefinition,
    sourceAreas: MetaElementVO[],
    mainArea: Connection,
    keepOriginalConnectionName: boolean
  ): ConnectionConfiguration {
    const mainConnectionDefinition: ConnectionDefinition = this.createMainConnectionDefinition(targetConnectionDefinition);
    return this.createConnectionConfiguration(mainConnectionDefinition, sourceAreas, mainArea, keepOriginalConnectionName);
  }

  private createInternalConnectionConfiguration(
    targetConnectionDefinition: ConnectionDefinition,
    sourceAreas: MetaElementVO[],
    mainArea: Connection,
    keepOriginalConnectionName: boolean
  ): ConnectionConfiguration {
    const internalConnectionDefinition: ConnectionDefinition = this.createInternalConnectionDefinition(targetConnectionDefinition);
    let internalConnectionConfiguration: ConnectionConfiguration = null;
    if (internalConnectionDefinition) {
      internalConnectionConfiguration = this.createConnectionConfiguration(
        internalConnectionDefinition,
        sourceAreas,
        mainArea,
        keepOriginalConnectionName
      );
    }
    return internalConnectionConfiguration;
  }

  private createConnectionConfiguration(
    connectionDefinition: ConnectionDefinition,
    sourceAreas: MetaElementVO[],
    mainArea: Connection,
    keepOriginalConnectionName: boolean
  ): ConnectionConfiguration {
    const createdMetaElement: MetaElementVO = this.createMetaElement(connectionDefinition, sourceAreas, mainArea, keepOriginalConnectionName);
    const createdConnectionMesh: Mesh = this.createConnectionMesh(
      connectionDefinition,
      mainArea,
      this._connectionCreator.isBothSidesConnection(createdMetaElement)
    );
    const connectionConfiguration: ConnectionConfiguration = new ConnectionConfiguration(createdMetaElement, createdConnectionMesh);

    let connectionName: string = createdMetaElement.name;
    if (StringUtils.isEmpty(connectionName)) {
      if (connectionDefinition.name) {
        // use name from connection-definition if it's defined there (e.g. in case of undo-redo)
        connectionName = connectionDefinition.name;
        if (connectionDefinition.isInternal) {
          connectionName = this._connectionCreator.toggleConnectionName(connectionName);
        }
      } else {
        // name of the main area might contain internal-postfix (e.g. when multipurpose area was created by conversion of texture area,
        // so name of texture area will be kept and internal-postfix will be applied to the main connection area)
        // such internal postfix (if it exists) should not be considered
        connectionName = MetaElementUtils.removeInternalConnectionPostfix(mainArea.metaElementVO.name);
        if (connectionDefinition.isInternal) {
          connectionName = MetaElementUtils.applyInternalConnectionPostfix(connectionName);
        }
      }
    }
    connectionConfiguration.updateConnectionName(connectionName);
    return connectionConfiguration;
  }

  private createMetaElement(
    connectionDefinition: ConnectionDefinition,
    sourceAreas: MetaElementVO[],
    mainArea: Connection,
    keepOriginalConnectionName: boolean
  ): MetaElementVO {
    let createdMetaElement: MetaElementVO = null;
    const article: ArticleVO = mainArea.articleContainer.article;
    const sourceArea: MetaElementVO = this._connectionCreator.findMetaElementByConnectionType(sourceAreas, connectionDefinition.type, false);
    if (sourceArea) {
      createdMetaElement = CloneUtils.cloneMetaElementVO(sourceArea);
      if (!keepOriginalConnectionName) {
        createdMetaElement.name = null;
      }
      createdMetaElement.isInternal = connectionDefinition.isInternal;
    } else {
      createdMetaElement = MetaElementUtils.createMetaElementByName(article, connectionDefinition, 1, false);
      // apply parameters available for all types of areas/meta-elements (specified in MetaElementVO)
      createdMetaElement.behaviorProperties = ConnectionInfoUtils.cloneProperties(mainArea.metaElementVO.behaviorProperties);
      createdMetaElement.corridor = mainArea.metaElementVO.corridor;
      createdMetaElement.orderNumber = mainArea.metaElementVO.orderNumber;
      createdMetaElement.name = null;
    }
    MetaElementUtils.applySubtypeToMetaElement(createdMetaElement, connectionDefinition.subtype);
    MetaElementUtils.removeSetIdFromMultipurposeArea(createdMetaElement);
    return createdMetaElement;
  }

  private createConnectionMesh(targetConnectionDefinition: ConnectionDefinition, originalConnection: Connection, bothSides: boolean): Mesh {
    let createdConnectionMesh: Mesh = null;
    if (originalConnection && originalConnection.mesh) {
      originalConnection.selected = false;

      createdConnectionMesh = originalConnection.mesh.clone(false) as Mesh;
      Object3DUtils.disposeAllChildren(createdConnectionMesh);
      MeshUtils.applyMeshScaleToGeometry(createdConnectionMesh);
      createdConnectionMesh.geometry.computeBoundingBox();
      createdConnectionMesh.geometry.computeBoundingSphere();

      createdConnectionMesh.material = this.getUpdatedConnectionMaterial(
        originalConnection.mesh.material as Material,
        targetConnectionDefinition,
        bothSides
      );

      if (targetConnectionDefinition.isInternal) {
        createdConnectionMesh.visible = false;
      }
    }
    return createdConnectionMesh;
  }

  public getUpdatedConnectionMaterial(oldMaterial: Material, connectionDefinition: ConnectionDefinition, bothSides: boolean): Material {
    let newMaterial: Material;
    if (
      (oldMaterial instanceof SpriteMaterial || (IsThreeJsType.meshPhongMaterial(oldMaterial) && IsThreeJsType.texture(oldMaterial.map))) &&
      !MetaElementUtils.isImageAllowedBySubtype(connectionDefinition.subtype)
    ) {
      newMaterial = this._connectionCreator.createColorMaterial('#ffffff', oldMaterial.opacity);
    } else {
      newMaterial = this.cloneConnectionMaterial(oldMaterial);
    }
    if (connectionDefinition.type === ConnectionAreaType.TEXTURE_AREA) {
      newMaterial.transparent = false;
      newMaterial.opacity = 1;
    }
    newMaterial.side = bothSides ? DoubleSide : FrontSide;
    newMaterial.needsUpdate = true;
    return newMaterial;
  }

  private cloneConnectionMaterial(material: Material): Material {
    if (!material) {
      return null;
    }
    const clonedMaterial: Material = material.clone();
    return clonedMaterial;
  }

  private createMainConnectionDefinition(targetConnectionDefinition: ConnectionDefinition): ConnectionDefinition {
    if (!targetConnectionDefinition) {
      return null;
    }
    return this._connectionCreator.getMainConnectionDefinition(
      targetConnectionDefinition.type,
      targetConnectionDefinition.subtype,
      targetConnectionDefinition.name
    );
  }

  private createInternalConnectionDefinition(targetConnectionDefinition: ConnectionDefinition): ConnectionDefinition {
    if (!targetConnectionDefinition) {
      return null;
    }
    return this._connectionCreator.getInternalConnectionDefinition(
      targetConnectionDefinition.type,
      targetConnectionDefinition.subtype,
      targetConnectionDefinition.name
    );
  }

  private isConnectionOfFormType(connection: Connection, form: ConnectionShapeType): boolean {
    if ((connection.meshIsCircle && form === ConnectionShapeType.CIRCLE) || (!connection.meshIsCircle && form === ConnectionShapeType.RECTANGLE)) {
      return true;
    }
    return false;
  }
}
